import { Const } from "./constants";
import { Beepers } from "./beepers";
import { Walls } from "./walls";
import { SquareColors } from "./squareColors";
import type { CanvasModel } from "./canvasModel";
import { assert } from "../util/util";

export interface KarelModelShape {
  dir: number;
  karelRow: number;
  karelCol: number;
  beepers: Beepers | null;
  walls: Walls | null;
  squareColors: SquareColors | null;
  rows: number;
  cols: number;
  deepCopy(): KarelModelShape;
  equals(otherModel: KarelModelShape): boolean;
  move(): void;
  turnLeft(): void;
  turnRight(): void;
  turnAround(): void;
  pickBeeper(): void;
  putBeeper(): void;
  paintCorner(color: string): void;
  getDirection(): number;
  getNumRows(): number;
  getNumCols(): number;
  getKarelRow(): number;
  getKarelCol(): number;
  getSquareColor(r: number, c: number): string | null;
  getNumBeepers(r: number, c: number): number;
  hasTopWall(r: number, c: number): boolean;
  hasRightWall(r: number, c: number): boolean;
  beeperPresent(): boolean;
  frontIsClear(): boolean;
  rightIsClear(): boolean;
  leftIsClear(): boolean;
  canMove(direction: string): boolean;
  facingNorth(): boolean;
  facingSouth(): boolean;
  facingEast(): boolean;
  facingWest(): boolean;
  loadWorld(worldText: string, canvasModel: CanvasModel): void;
}

export function KarelModel(): KarelModelShape {
  const that: KarelModelShape = {
    dir: Const.KAREL_EAST,
    karelRow: 0,
    karelCol: 0,
    beepers: null,
    walls: null,
    squareColors: null,
    rows: 0,
    cols: 0,

    deepCopy(): KarelModelShape {
      const newModel = KarelModel();
      newModel.dir = that.dir;
      newModel.karelRow = that.karelRow;
      newModel.karelCol = that.karelCol;
      newModel.beepers = that.beepers!.deepCopy();
      newModel.walls = that.walls!.deepCopy();
      newModel.squareColors = that.squareColors!.deepCopy();
      newModel.rows = that.rows;
      newModel.cols = that.cols;
      return newModel;
    },

    equals(otherModel: KarelModelShape): boolean {
      if (that.dir !== otherModel.dir) return false;
      if (that.karelRow !== otherModel.karelRow) return false;
      if (that.karelCol !== otherModel.karelCol) return false;
      if (!that.beepers!.equals(otherModel.beepers!)) return false;
      if (!that.squareColors!.equals(otherModel.squareColors!)) return false;
      return true;
    },

    move(): void {
      let newRow = that.karelRow;
      let newCol = that.karelCol;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newCol += 1;
          break;
        case Const.KAREL_WEST:
          newCol -= 1;
          break;
        case Const.KAREL_NORTH:
          newRow -= 1;
          break;
        case Const.KAREL_SOUTH:
          newRow += 1;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      if (
        that.walls!.isMoveValid(that.karelRow, that.karelCol, newRow, newCol)
      ) {
        that.karelRow = newRow;
        that.karelCol = newCol;
      } else {
        throw new Error("Front Is Blocked");
      }
    },

    turnLeft(): void {
      let newD = that.dir;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newD = Const.KAREL_NORTH;
          break;
        case Const.KAREL_WEST:
          newD = Const.KAREL_SOUTH;
          break;
        case Const.KAREL_NORTH:
          newD = Const.KAREL_WEST;
          break;
        case Const.KAREL_SOUTH:
          newD = Const.KAREL_EAST;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      that.dir = newD;
    },

    turnRight(): void {
      let newD = that.dir;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newD = Const.KAREL_SOUTH;
          break;
        case Const.KAREL_WEST:
          newD = Const.KAREL_NORTH;
          break;
        case Const.KAREL_NORTH:
          newD = Const.KAREL_EAST;
          break;
        case Const.KAREL_SOUTH:
          newD = Const.KAREL_WEST;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      that.dir = newD;
    },

    turnAround(): void {
      let newD = that.dir;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newD = Const.KAREL_WEST;
          break;
        case Const.KAREL_WEST:
          newD = Const.KAREL_EAST;
          break;
        case Const.KAREL_NORTH:
          newD = Const.KAREL_SOUTH;
          break;
        case Const.KAREL_SOUTH:
          newD = Const.KAREL_NORTH;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      that.dir = newD;
    },

    pickBeeper(): void {
      if (that.beepers!.beeperPresent(that.karelRow, that.karelCol)) {
        that.beepers!.pickBeeper(that.karelRow, that.karelCol);
      } else {
        throw new Error("No Beepers Present");
      }
    },

    putBeeper(): void {
      that.beepers!.putBeeper(that.karelRow, that.karelCol);
    },

    paintCorner(color: string): void {
      that.squareColors!.paintCorner(that.karelRow, that.karelCol, color);
    },

    getDirection: () => that.dir,
    getNumRows: () => that.rows,
    getNumCols: () => that.cols,
    getKarelRow: () => that.karelRow,
    getKarelCol: () => that.karelCol,
    getSquareColor: (r: number, c: number) => that.squareColors!.getColor(r, c),
    getNumBeepers: (r: number, c: number) => that.beepers!.getNumBeepers(r, c),
    hasTopWall: (r: number, c: number) => that.walls!.topWall(r, c),
    hasRightWall: (r: number, c: number) => that.walls!.rightWall(r, c),
    beeperPresent: () =>
      that.beepers!.beeperPresent(that.karelRow, that.karelCol),

    frontIsClear(): boolean {
      let newRow = that.karelRow;
      let newCol = that.karelCol;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newCol += 1;
          break;
        case Const.KAREL_WEST:
          newCol -= 1;
          break;
        case Const.KAREL_NORTH:
          newRow -= 1;
          break;
        case Const.KAREL_SOUTH:
          newRow += 1;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      return that.walls!.isMoveValid(
        that.karelRow,
        that.karelCol,
        newRow,
        newCol,
      );
    },

    rightIsClear(): boolean {
      let newRow = that.karelRow;
      let newCol = that.karelCol;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newRow += 1;
          break;
        case Const.KAREL_WEST:
          newRow -= 1;
          break;
        case Const.KAREL_NORTH:
          newCol += 1;
          break;
        case Const.KAREL_SOUTH:
          newCol -= 1;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      return that.walls!.isMoveValid(
        that.karelRow,
        that.karelCol,
        newRow,
        newCol,
      );
    },

    leftIsClear(): boolean {
      let newRow = that.karelRow;
      let newCol = that.karelCol;
      switch (that.dir) {
        case Const.KAREL_EAST:
          newRow -= 1;
          break;
        case Const.KAREL_WEST:
          newRow += 1;
          break;
        case Const.KAREL_NORTH:
          newCol -= 1;
          break;
        case Const.KAREL_SOUTH:
          newCol += 1;
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      return that.walls!.isMoveValid(
        that.karelRow,
        that.karelCol,
        newRow,
        newCol,
      );
    },

    canMove(direction: string): boolean {
      let newRow = that.karelRow;
      let newCol = that.karelCol;
      const d = direction.toLowerCase();
      switch (that.dir) {
        case Const.KAREL_EAST:
          if (d === "forward") newCol += 1;
          else if (d === "backward") newCol -= 1;
          else if (d === "left") newRow -= 1;
          else if (d === "right") newRow += 1;
          else throw new Error("invalid direction: " + direction);
          break;
        case Const.KAREL_WEST:
          if (d === "forward") newCol -= 1;
          else if (d === "backward") newCol += 1;
          else if (d === "left") newRow += 1;
          else if (d === "right") newRow -= 1;
          else throw new Error("invalid direction: " + direction);
          break;
        case Const.KAREL_NORTH:
          if (d === "forward") newRow -= 1;
          else if (d === "backward") newRow += 1;
          else if (d === "left") newCol -= 1;
          else if (d === "right") newCol += 1;
          else throw new Error("invalid direction: " + direction);
          break;
        case Const.KAREL_SOUTH:
          if (d === "forward") newRow += 1;
          else if (d === "backward") newRow -= 1;
          else if (d === "left") newCol += 1;
          else if (d === "right") newCol -= 1;
          else throw new Error("invalid direction: " + direction);
          break;
        default:
          throw new Error("invalid dir: " + that.dir);
      }
      return that.walls!.isMoveValid(
        that.karelRow,
        that.karelCol,
        newRow,
        newCol,
      );
    },

    facingNorth: () => that.dir === Const.KAREL_NORTH,
    facingSouth: () => that.dir === Const.KAREL_SOUTH,
    facingEast: () => that.dir === Const.KAREL_EAST,
    facingWest: () => that.dir === Const.KAREL_WEST,

    loadWorld(worldText: string, canvasModel: CanvasModel): void {
      const lines = worldText.split("\n");

      loadDimensionLine(lines[0]);

      that.beepers = Beepers(that.rows, that.cols);
      that.walls = Walls(that.rows, that.cols);
      that.squareColors = SquareColors(that.rows, that.cols);

      that.dir = Const.KAREL_EAST;
      placeKarel(that.rows - 1, 0);

      for (let i = 1; i < lines.length; i++) {
        if (lines[i] !== "") {
          loadLine(lines[i]);
        }
      }

      canvasModel.setKarelDimensions(that.rows, that.cols);
    },
  };

  function extractCoord(coordString: string): [number, number] {
    const rParenIndex = coordString.indexOf("(");
    const lParenIndex = coordString.indexOf(")");
    const inner = coordString.substring(rParenIndex + 1, lParenIndex);
    const coordStrings = inner.split(",");
    return [parseInt(coordStrings[0]), parseInt(coordStrings[1])];
  }

  function loadDimensionLine(line: string): void {
    const dimensionStrings = line.split(":");
    assert(dimensionStrings[0].trim() === "Dimension", "World file malformed");
    const coord = extractCoord(dimensionStrings[1]);
    that.rows = coord[0];
    that.cols = coord[1];
  }

  function placeKarel(row: number, col: number): void {
    that.karelRow = row;
    that.karelCol = col;
  }

  function loadWallLine(line: string): void {
    const coord = extractCoord(line);
    if (line.indexOf("west") !== -1) {
      const row = that.rows - coord[1];
      const col = coord[0] - 2;
      that.walls!.addRightWall(row, col);
    } else {
      const row = that.rows - coord[1] + 1;
      const col = coord[0] - 1;
      that.walls!.addTopWall(row, col);
    }
  }

  function loadBeeperLine(line: string): void {
    const coord = extractCoord(line);
    const row = that.rows - coord[0];
    const col = coord[1] - 1;
    that.beepers!.putBeeper(row, col);
  }

  /**
   * Parses a `Color: (street, avenue) <css-color>` line. The color is any CSS
   * color string accepted by the canvas (e.g. `red`, `#ff8800`, `rgb(0,0,0)`),
   * matching the values students pass to `paintCorner`.
   */
  function loadColorLine(line: string): void {
    const coord = extractCoord(line);
    const row = that.rows - coord[0];
    const col = coord[1] - 1;
    const color = line.substring(line.indexOf(")") + 1).trim();
    assert(color !== "", "Color world line missing color value");
    that.squareColors!.paintCorner(row, col, color);
  }

  function loadKarelLine(line: string): void {
    const coord = extractCoord(line);
    const row = that.rows - coord[0];
    const col = coord[1] - 1;
    placeKarel(row, col);
    if (line.indexOf("west") !== -1) that.dir = Const.KAREL_WEST;
    if (line.indexOf("south") !== -1) that.dir = Const.KAREL_SOUTH;
    if (line.indexOf("north") !== -1) that.dir = Const.KAREL_NORTH;
  }

  function loadLine(line: string): void {
    const elements = line.split(":");
    assert(elements.length === 2, line + "World file missing :");
    const key = elements[0];
    if (key === "Karel") {
      loadKarelLine(elements[1]);
    } else if (key === "Wall") {
      loadWallLine(elements[1]);
    } else if (key === "Beeper") {
      loadBeeperLine(elements[1]);
    } else if (key === "Color") {
      loadColorLine(elements[1]);
    }
  }

  return that;
}
